import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CaretLeft, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { inr, ACCOUNT_TYPE_LABELS } from "../lib/format";
import { TxRow } from "../components/TxRow";

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [lookups, setLookups] = useState(null);

  useEffect(() => {
    api.get(`/accounts/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Not found"));
    api.get("/lookups").then((r) => setLookups(r.data)).catch(() => {});
  }, [id]);

  const catNames = useMemo(() => Object.fromEntries((lookups?.categories || []).map((c) => [c.id, c.name])), [lookups]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const a = data.account;
  const isCard = a.account_type === "credit_card";

  const remove = async () => {
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Account deleted");
      navigate("/more/accounts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="px-5 pt-6 pb-10" data-testid="account-detail-page">
      <div className="flex items-center justify-between">
        <button data-testid="account-detail-back" onClick={() => navigate(-1)} className="h-11 w-11 -ml-2 flex items-center justify-center rounded-xl active:bg-secondary"><CaretLeft size={20} /></button>
        <button data-testid="account-delete-btn" onClick={remove} className="h-11 px-3 rounded-xl text-destructive text-sm flex items-center gap-1"><Trash size={16} /> Delete</button>
      </div>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mt-2">{ACCOUNT_TYPE_LABELS[a.account_type]}{a.institution_name ? ` · ${a.institution_name}` : ""}</p>
      <h1 className="text-2xl font-heading font-bold tracking-tight">{a.name}</h1>
      <p className={`mt-2 text-4xl font-light tracking-tight tnum ${isCard ? "text-expense" : ""}`}>
        {isCard ? inr(a.outstanding) : inr(a.current_balance)}
      </p>
      <p className="text-sm text-muted-foreground">{isCard ? "Outstanding payable" : "Current balance"}</p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="bg-white border border-border rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Total in</p>
          <p className="mt-1 text-lg font-medium tnum text-income">{inr(data.inflow)}</p>
        </div>
        <div className="bg-white border border-border rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Total out</p>
          <p className="mt-1 text-lg font-medium tnum text-expense">{inr(data.outflow)}</p>
        </div>
        {isCard && a.credit_limit != null && (
          <div className="bg-white border border-border rounded-2xl p-4 col-span-2">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Available credit</p>
            <p className="mt-1 text-lg font-medium tnum">{inr(a.available_credit)} <span className="text-xs text-muted-foreground">of {inr(a.credit_limit)}</span></p>
            {(a.statement_day || a.payment_due_day) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {a.statement_day ? `Statement day ${a.statement_day}` : ""}{a.statement_day && a.payment_due_day ? " · " : ""}{a.payment_due_day ? `Due day ${a.payment_due_day}` : ""}
              </p>
            )}
          </div>
        )}
      </div>

      <h2 className="mt-6 text-lg font-heading font-semibold">Transactions</h2>
      <div className="mt-2 bg-white border border-border rounded-2xl px-4 py-1">
        {data.transactions.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground text-center">No transactions on this account yet.</p>
        ) : (
          data.transactions.map((tx) => <TxRow key={tx.id} tx={tx} categories={catNames} />)
        )}
      </div>
    </div>
  );
}
